import { useEffect, useState } from "react";
import { History, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, openBlob } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/status";
import { StatusChip } from "@/components/PacketList";

export function useSigningHistory(patientId, packetId, bump) {
  const [h, setH] = useState(null);
  useEffect(() => {
    let live = true;
    if (patientId) api.get(`/patients/${patientId}/signing-history`, { params: { exclude: packetId } }).then((r) => { if (live) setH(r.data); }).catch(() => { if (live) setH(null); });
    return () => { live = false; };
  }, [patientId, packetId, bump]);
  return patientId ? h : null;
}

const STATE = { active: "still valid", expired: "expired", revoked: "revoked" };

export function LastSigned({ info, testid, formName }) {
  if (!info) return null;
  const ok = info.state === "active";
  const other = formName && info.template_name && info.template_name !== formName;
  return (
    <p className={`text-xs mt-0.5 ${ok ? "text-green-700" : "text-red-700"}`} data-testid={testid}>
      {other ? `Covered by ${info.template_name}, signed` : "Last signed:"} {fmtDate(info.signed_at)} - {STATE[info.state]}{ok ? ` until ${fmtDate(info.expires_at)}` : ""}
    </p>
  );
}

export function SigningHistory({ history, patientName }) {
  const [open, setOpen] = useState(false);
  if (!history) return null;
  return (
    <>
      <button type="button" className="mt-3 inline-flex items-center gap-1 text-xs text-brand underline" onClick={() => setOpen(true)} data-testid="signing-history-link">
        <History className="w-3.5 h-3.5" />Signing history ({history.packets.length} earlier packet{history.packets.length === 1 ? "" : "s"})
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="signing-history-panel">
          <SheetHeader><SheetTitle>Signing history</SheetTitle><SheetDescription>{patientName} - every earlier packet, newest first</SheetDescription></SheetHeader>
          {history.packets.length === 0 && <p className="text-sm text-slate-500 mt-6" data-testid="signing-history-empty">No earlier packets for this patient.</p>}
          <ol className="mt-4 space-y-3">
            {history.packets.map((p) => (
              <li key={p.id} className="border rounded-lg p-3 text-sm" data-testid={`history-packet-${p.id}`}>
                <div className="flex items-start justify-between gap-2"><span className="font-medium">{p.title}</span><StatusChip s={p.status} /></div>
                <ul className="text-xs text-slate-600 mt-1">{p.documents.map((d, i) => <li key={i}>{d.template_name} v{d.template_version}</li>)}</ul>
                <p className="text-xs text-slate-500 mt-1">Created {fmt(p.created_at)}{p.signed_at ? ` - signed ${fmt(p.signed_at)}` : ""}{p.outcome ? ` - ${p.outcome.replace(/_/g, " ")}` : ""}</p>
                {p.has_pdf && <Button size="sm" variant="outline" className="mt-2 h-8" onClick={() => openBlob(`/packets/${p.id}/final.pdf`)} data-testid={`history-view-pdf-${p.id}`}><FileText className="w-3.5 h-3.5 mr-1" />View PDF</Button>}
              </li>
            ))}
          </ol>
        </SheetContent>
      </Sheet>
    </>
  );
}
